import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plus, Pencil, Trash2, Clock, MessageSquare, Image, Zap, Mic, Sticker, Settings2,
  ArrowLeft, HelpCircle, Save, Loader2, Lock, ChevronRight,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import type { Tables } from "@/integrations/supabase/types";

type WarmupProfile = Tables<"warmup_profiles">;

// ── Form state shape (maps to DB columns) ──
interface ProfileForm {
  name: string;
  ui_icon: string;
  description: string;
  min_daily_msgs: number;
  max_daily_msgs: number;
  period_start: string;
  period_end: string;
  interval_min_sec: number;
  interval_max_sec: number;
  prob_text: number;
  prob_audio: number;
  prob_image: number;
  prob_sticker: number;
  prob_react: number;
  strangers_per_day: number;
  status_enabled: boolean;
  status_per_day: number;
}

const defaultForm: ProfileForm = {
  name: "",
  ui_icon: "⚙️",
  description: "",
  min_daily_msgs: 30,
  max_daily_msgs: 80,
  period_start: "08:00",
  period_end: "20:00",
  interval_min_sec: 60,
  interval_max_sec: 300,
  prob_text: 70,
  prob_audio: 15,
  prob_image: 10,
  prob_sticker: 5,
  prob_react: 30,
  strangers_per_day: 3,
  status_enabled: false,
  status_per_day: 0,
};

function dbToForm(p: WarmupProfile): ProfileForm {
  return {
    name: p.name,
    ui_icon: p.ui_icon || "⚙️",
    description: p.description || "",
    min_daily_msgs: p.min_daily_msgs || 30,
    max_daily_msgs: p.max_daily_msgs || 80,
    period_start: (p.period_start || "08:00:00").slice(0, 5),
    period_end: (p.period_end || "20:00:00").slice(0, 5),
    interval_min_sec: p.interval_min_sec || 60,
    interval_max_sec: p.interval_max_sec || 300,
    prob_text: p.prob_text || 0,
    prob_audio: p.prob_audio || 0,
    prob_image: p.prob_image || 0,
    prob_sticker: p.prob_sticker || 0,
    prob_react: p.prob_react || 30,
    strangers_per_day: p.strangers_per_day || 0,
    status_enabled: (p as any).status_enabled ?? false,
    status_per_day: (p as any).status_per_day ?? 0,
  };
}

const presetTemplates: { id: string; emoji: string; name: string; desc: string; values: ProfileForm }[] = [
  {
    id: "t-cold", emoji: "❄️", name: "Frio / Recém-nascido", desc: "Chips 0–7 dias. Segurança extrema.",
    values: { ...defaultForm, ui_icon: "❄️", description: "Baseado no preset Frio", min_daily_msgs: 15, max_daily_msgs: 40, period_start: "09:00", period_end: "19:00", interval_min_sec: 300, interval_max_sec: 900, prob_text: 90, prob_audio: 5, prob_image: 0, prob_sticker: 5, strangers_per_day: 1, status_enabled: false, status_per_day: 0 },
  },
  {
    id: "t-warm", emoji: "🟡", name: "Morno / Maturação", desc: "Chips 8–15 dias. Uso ativo normal.",
    values: { ...defaultForm, ui_icon: "🟡", description: "Baseado no preset Morno", min_daily_msgs: 50, max_daily_msgs: 120, period_start: "08:00", period_end: "22:00", interval_min_sec: 60, interval_max_sec: 300, prob_text: 70, prob_audio: 15, prob_image: 10, prob_sticker: 5, strangers_per_day: 3, status_enabled: true, status_per_day: 2 },
  },
  {
    id: "t-hot", emoji: "🔥", name: "Quente / Blindagem", desc: "Chips 15+ dias. Tráfego pesado.",
    values: { ...defaultForm, ui_icon: "🔥", description: "Baseado no preset Quente", min_daily_msgs: 150, max_daily_msgs: 300, period_start: "07:00", period_end: "23:30", interval_min_sec: 15, interval_max_sec: 90, prob_text: 50, prob_audio: 25, prob_image: 15, prob_sticker: 10, strangers_per_day: 5, status_enabled: true, status_per_day: 4 },
  },
  {
    id: "t-maintenance", emoji: "🛡️", name: "Manutenção / Terapia", desc: "Chips em operação. Reequilibrar trust.",
    values: { ...defaultForm, ui_icon: "🛡️", description: "Baseado no preset Manutenção", min_daily_msgs: 30, max_daily_msgs: 80, period_start: "08:00", period_end: "20:00", interval_min_sec: 120, interval_max_sec: 600, prob_text: 40, prob_audio: 30, prob_image: 0, prob_sticker: 30, strangers_per_day: 0, status_enabled: true, status_per_day: 1 },
  },
];

const emojiOptions = ["⚙️", "🚀", "💎", "🎯", "🧪", "🔧", "📈", "🌀", "⭐", "🏷️"];

// ── Tooltip helper ──
function Tip({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60 hover:text-muted-foreground cursor-help shrink-0" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px] text-xs">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

// ── Full-page Form ──
function ProfileFormPage({
  profile,
  onChange,
  onSave,
  onCancel,
  isEditing,
  isSaving,
}: {
  profile: ProfileForm;
  onChange: (p: ProfileForm) => void;
  onSave: () => void;
  onCancel: () => void;
  isEditing: boolean;
  isSaving: boolean;
}) {
  const totalMedia = profile.prob_text + profile.prob_audio + profile.prob_image + profile.prob_sticker;
  const mediaWarning = totalMedia !== 100;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onCancel} className="h-9 w-9">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-display text-xl md:text-2xl font-bold text-foreground">
              {isEditing ? "Editar Perfil" : "Novo Perfil de Aquecimento"}
            </h1>
            <p className="text-muted-foreground text-sm">Configure cada detalhe do comportamento de warmup.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button onClick={onSave} className="gap-2 glow-orange-sm" disabled={!profile.name.trim() || mediaWarning || isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isEditing ? "Salvar" : "Criar Perfil"}
          </Button>
        </div>
      </div>

      {/* Template selector */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="glass-card border-warmz-glass-border/50">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-semibold">Usar Preset como Base</Label>
              <Tip text="Selecione um dos presets pré-definidos para preencher todos os campos automaticamente. Depois você pode ajustar cada valor." />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {presetTemplates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onChange({ ...t.values, name: profile.name || "" })}
                  className="flex items-center gap-3 p-3 rounded-xl border border-warmz-glass-border/30 bg-secondary/20 hover:bg-secondary/40 hover:border-primary/30 transition-all text-left"
                >
                  <span className="text-2xl">{t.emoji}</span>
                  <div>
                    <p className="text-sm font-medium text-foreground">{t.name}</p>
                    <p className="text-[11px] text-muted-foreground">{t.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column */}
        <div className="space-y-6">
          {/* Identity */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-5 space-y-4">
                <Label className="text-sm font-semibold">Identidade do Perfil</Label>
                <div className="flex gap-3">
                  <div className="space-y-2 flex-1">
                    <Label className="text-xs text-muted-foreground">Nome</Label>
                    <Input
                      value={profile.name}
                      onChange={(e) => onChange({ ...profile, name: e.target.value })}
                      placeholder="Ex: Lançamento Agressivo"
                      className="bg-secondary/40 border-warmz-glass-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Emoji</Label>
                    <div className="flex gap-1 flex-wrap max-w-[180px]">
                      {emojiOptions.map((e) => (
                        <button
                          key={e}
                          type="button"
                          onClick={() => onChange({ ...profile, ui_icon: e })}
                          className={`w-8 h-8 rounded-lg text-base flex items-center justify-center transition-all ${
                            profile.ui_icon === e
                              ? "bg-primary/20 ring-1 ring-primary"
                              : "bg-secondary/30 hover:bg-secondary/50"
                          }`}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Descrição</Label>
                  <Input
                    value={profile.description}
                    onChange={(e) => onChange({ ...profile, description: e.target.value })}
                    placeholder="Breve descrição do objetivo deste perfil"
                    className="bg-secondary/40 border-warmz-glass-border"
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Volume */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-primary" />
                  <Label className="text-sm font-semibold">Volume de Mensagens / dia</Label>
                  <Tip text="Quantidade total de mensagens (enviadas + recebidas) que o chip processará por dia. O sistema varia entre o mínimo e máximo para parecer mais natural." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <span className="text-xs text-muted-foreground">Mínimo</span>
                    <div className="flex items-center gap-2">
                      <Slider value={[profile.min_daily_msgs]} onValueChange={([v]) => onChange({ ...profile, min_daily_msgs: v })} min={5} max={500} step={5} className="flex-1" />
                      <span className="text-sm font-mono w-10 text-right">{profile.min_daily_msgs}</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-xs text-muted-foreground">Máximo</span>
                    <div className="flex items-center gap-2">
                      <Slider value={[profile.max_daily_msgs]} onValueChange={([v]) => onChange({ ...profile, max_daily_msgs: v })} min={5} max={500} step={5} className="flex-1" />
                      <span className="text-sm font-mono w-10 text-right">{profile.max_daily_msgs}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Schedule */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <Label className="text-sm font-semibold">Horário de Funcionamento</Label>
                  <Tip text="Janela de horário em que o chip ficará ativo enviando e recebendo mensagens. Fora deste período, o chip fica em silêncio — simulando uma pessoa dormindo." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <span className="text-xs text-muted-foreground">Início</span>
                    <Input type="time" value={profile.period_start} onChange={(e) => onChange({ ...profile, period_start: e.target.value })} className="bg-secondary/40 border-warmz-glass-border" />
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-xs text-muted-foreground">Fim</span>
                    <Input type="time" value={profile.period_end} onChange={(e) => onChange({ ...profile, period_end: e.target.value })} className="bg-secondary/40 border-warmz-glass-border" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Delays */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-primary" />
                  <Label className="text-sm font-semibold">Delays entre Mensagens</Label>
                  <Tip text="Tempo de espera entre cada mensagem enviada. Delays curtos simulam conversas empolgadas. Delays longos simulam alguém ocupado. O sistema escolhe um valor aleatório entre o mínimo e máximo." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <span className="text-xs text-muted-foreground">Mínimo</span>
                    <div className="flex items-center gap-2">
                      <Slider value={[profile.interval_min_sec]} onValueChange={([v]) => onChange({ ...profile, interval_min_sec: v })} min={5} max={900} step={5} className="flex-1" />
                      <span className="text-sm font-mono w-12 text-right">{profile.interval_min_sec}s</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <span className="text-xs text-muted-foreground">Máximo</span>
                    <div className="flex items-center gap-2">
                      <Slider value={[profile.interval_max_sec]} onValueChange={([v]) => onChange({ ...profile, interval_max_sec: v })} min={5} max={900} step={5} className="flex-1" />
                      <span className="text-sm font-mono w-12 text-right">{profile.interval_max_sec}s</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Media Mix */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Image className="h-4 w-4 text-primary" />
                    <Label className="text-sm font-semibold">Mix de Mídia</Label>
                    <Tip text="Distribuição percentual dos tipos de conteúdo enviados. O total deve somar 100%. Variar os tipos de mídia faz o chip parecer mais humano para o WhatsApp." />
                  </div>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      mediaWarning ? "border-destructive/50 text-destructive" : "border-accent/50 text-accent"
                    }`}
                  >
                    Total: {totalMedia}%{mediaWarning ? " ⚠️" : " ✓"}
                  </Badge>
                </div>
                {[
                  { key: "prob_text" as const, label: "Texto", icon: MessageSquare, tip: "Mensagens de texto simples — o tipo mais seguro e natural." },
                  { key: "prob_audio" as const, label: "Áudio", icon: Mic, tip: "Notas de voz geradas por IA. Aumenta muito a credibilidade do chip." },
                  { key: "prob_image" as const, label: "Imagens", icon: Image, tip: "Fotos e imagens aleatórias trocadas entre chips." },
                  { key: "prob_sticker" as const, label: "Figurinhas", icon: Sticker, tip: "Stickers do WhatsApp. Simulam uso casual e descontraído." },
                ].map((item) => (
                  <div key={item.key} className="flex items-center gap-3">
                    <item.icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-xs text-muted-foreground w-16">{item.label}</span>
                    <Tip text={item.tip} />
                    <Slider value={[profile[item.key]]} onValueChange={([v]) => onChange({ ...profile, [item.key]: v })} min={0} max={100} step={5} className="flex-1" />
                    <span className="text-sm font-mono w-10 text-right">{profile[item.key]}%</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>

          {/* Extra settings */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <Settings2 className="h-4 w-4 text-primary" />
                  <Label className="text-sm font-semibold">Configurações Avançadas</Label>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Estranhos/dia</span>
                    <Tip text="Quantos números desconhecidos o chip vai interagir por dia. Simula o comportamento de conhecer pessoas novas. Valores altos podem chamar atenção." />
                  </div>
                  <div className="flex items-center gap-2">
                    <Slider value={[profile.strangers_per_day]} onValueChange={([v]) => onChange({ ...profile, strangers_per_day: v })} min={0} max={20} step={1} className="flex-1" />
                    <span className="text-sm font-mono w-6 text-right">{profile.strangers_per_day}</span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Probabilidade de Reagir (%)</span>
                    <Tip text="Chance de reagir a mensagens recebidas com emoji reactions. Aumenta a naturalidade da conversa." />
                  </div>
                  <div className="flex items-center gap-2">
                    <Slider value={[profile.prob_react]} onValueChange={([v]) => onChange({ ...profile, prob_react: v })} min={0} max={100} step={5} className="flex-1" />
                    <span className="text-sm font-mono w-10 text-right">{profile.prob_react}%</span>
                  </div>
                </div>
                {/* Status / Stories */}
                <div className="space-y-3 pt-2 border-t border-warmz-glass-border/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground">Publicar Status / Stories</span>
                      <Tip text="Publica automaticamente status (stories) no WhatsApp ao longo do dia. Aumenta a credibilidade e simula uso real do celular." />
                    </div>
                    <Switch
                      checked={profile.status_enabled}
                      onCheckedChange={(v) => onChange({ ...profile, status_enabled: v, status_per_day: v ? Math.max(profile.status_per_day, 1) : 0 })}
                    />
                  </div>
                  {profile.status_enabled && (
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">Publicações por dia</span>
                        <Tip text="Quantidade de status que serão publicados ao longo do período ativo. Distribuídos aleatoriamente." />
                      </div>
                      <div className="flex items-center gap-2">
                        <Slider value={[profile.status_per_day]} onValueChange={([v]) => onChange({ ...profile, status_per_day: v })} min={1} max={10} step={1} className="flex-1" />
                        <span className="text-sm font-mono w-6 text-right">{profile.status_per_day}</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Bottom save bar */}
      <div className="flex justify-end gap-2 pb-4">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={onSave} className="gap-2 glow-orange-sm" disabled={!profile.name.trim() || mediaWarning || isSaving}>
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {isEditing ? "Salvar Alterações" : "Criar Perfil"}
        </Button>
      </div>
    </div>
  );
}

// ── Main Page ──
export default function DashboardProfiles() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [view, setView] = useState<"list" | "form">("list");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProfileForm>(defaultForm);

  // Fetch all visible profiles (system + own)
  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ["warmup_profiles", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("warmup_profiles")
        .select("*")
        .or(`user_id.is.null,user_id.eq.${user!.id}`)
        .order("chain_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as WarmupProfile[];
    },
    enabled: !!user?.id,
  });

  const systemProfiles = profiles.filter((p) => p.is_system_preset);
  const customProfiles = profiles.filter((p) => !p.is_system_preset);

  const createMutation = useMutation({
    mutationFn: async (data: ProfileForm) => {
      const { error } = await supabase.from("warmup_profiles").insert({
        user_id: user!.id,
        name: data.name,
        ui_icon: data.ui_icon,
        description: data.description,
        min_daily_msgs: data.min_daily_msgs,
        max_daily_msgs: data.max_daily_msgs,
        period_start: data.period_start + ":00",
        period_end: data.period_end + ":00",
        interval_min_sec: data.interval_min_sec,
        interval_max_sec: data.interval_max_sec,
        prob_text: data.prob_text,
        prob_audio: data.prob_audio,
        prob_image: data.prob_image,
        prob_sticker: data.prob_sticker,
        prob_react: data.prob_react,
        strangers_per_day: data.strangers_per_day,
        status_enabled: data.status_enabled,
        status_per_day: data.status_per_day,
        is_system_preset: false,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmup_profiles"] });
      toast({ title: "Perfil criado com sucesso!" });
      setView("list");
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao criar perfil", description: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ProfileForm }) => {
      const { error } = await supabase
        .from("warmup_profiles")
        .update({
          name: data.name,
          ui_icon: data.ui_icon,
          description: data.description,
          min_daily_msgs: data.min_daily_msgs,
          max_daily_msgs: data.max_daily_msgs,
          period_start: data.period_start + ":00",
          period_end: data.period_end + ":00",
          interval_min_sec: data.interval_min_sec,
          interval_max_sec: data.interval_max_sec,
          prob_text: data.prob_text,
          prob_audio: data.prob_audio,
          prob_image: data.prob_image,
          prob_sticker: data.prob_sticker,
          prob_react: data.prob_react,
          strangers_per_day: data.strangers_per_day,
          status_enabled: data.status_enabled,
          status_per_day: data.status_per_day,
        } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmup_profiles"] });
      toast({ title: "Perfil atualizado!" });
      setView("list");
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao atualizar", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("warmup_profiles").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warmup_profiles"] });
      toast({ title: "Perfil removido!" });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao remover", description: err.message, variant: "destructive" });
    },
  });

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm);
    setView("form");
  };

  const openEdit = (p: WarmupProfile) => {
    setEditingId(p.id);
    setForm(dbToForm(p));
    setView("form");
  };

  const save = () => {
    if (!form.name.trim()) return;
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // ── Form View ──
  if (view === "form") {
    return (
      <ProfileFormPage
        profile={form}
        onChange={setForm}
        onSave={save}
        onCancel={() => setView("list")}
        isEditing={!!editingId}
        isSaving={isSaving}
      />
    );
  }

  // ── List View ──
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Perfis de Aquecimento</h1>
          <p className="text-muted-foreground text-sm mt-1">Gerencie presets do sistema e crie modos customizados.</p>
        </div>
        <Button className="gap-2 glow-orange-sm" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          Novo Perfil
        </Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="glass-card border-warmz-glass-border/50">
              <CardContent className="p-5 space-y-3">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-32" />
                <div className="grid grid-cols-3 gap-2">
                  <Skeleton className="h-12" />
                  <Skeleton className="h-12" />
                  <Skeleton className="h-12" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <>
          {/* System Presets — Timeline */}
          {systemProfiles.length > 0 && (
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Esteira de Aquecimento</p>
              <div className="relative">
                {systemProfiles.map((p, i) => {
                  const isLast = i === systemProfiles.length - 1;
                  const phaseNum = (p.chain_order ?? i) + 1;
                  return (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.08 }}
                      className="relative flex gap-4"
                    >
                      {/* Timeline spine */}
                      <div className="flex flex-col items-center shrink-0 w-10">
                        <div className="w-9 h-9 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center text-sm font-bold text-primary z-10">
                          {phaseNum}
                        </div>
                        {!isLast && (
                          <div className="flex-1 w-0.5 bg-primary/20 min-h-[16px]" />
                        )}
                      </div>

                      {/* Card */}
                      <div className="flex-1 pb-4">
                        <Card className="glass-card border-warmz-glass-border/50">
                          <CardContent className="p-5 space-y-3">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">{p.ui_icon || "⚙️"}</span>
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-semibold text-foreground">{p.name}</h3>
                                    <Badge variant="outline" className="text-[10px] border-warmz-glass-border/50 gap-1">
                                      <Lock className="h-2.5 w-2.5" /> Sistema
                                    </Badge>
                                    <Badge variant="secondary" className="text-[10px]">Fase {phaseNum}</Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {p.description || p.target_days_label || "Preset do sistema"}
                                    {" · "}
                                    <span className="font-medium">{p.duration_days ? `${p.duration_days} dias` : "Tempo indeterminado"}</span>
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              {[
                                { label: "Volume", value: `${p.min_daily_msgs}–${p.max_daily_msgs}/dia` },
                                { label: "Horário", value: `${(p.period_start || "08:00").slice(0, 5)}–${(p.period_end || "20:00").slice(0, 5)}` },
                                { label: "Delays", value: `${p.interval_min_sec}s–${p.interval_max_sec}s` },
                              ].map((d) => (
                                <div key={d.label} className="text-center p-2 rounded-lg bg-secondary/30">
                                  <p className="text-[10px] text-muted-foreground uppercase">{d.label}</p>
                                  <p className="text-xs font-medium text-foreground">{d.value}</p>
                                </div>
                              ))}
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex gap-2 flex-wrap">
                                <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[10px] border-warmz-glass-border/50 cursor-help">📝 {p.prob_text}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Texto — mensagens de texto simples</TooltipContent></Tooltip>
                                <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[10px] border-warmz-glass-border/50 cursor-help">🎙️ {p.prob_audio}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Áudio — notas de voz</TooltipContent></Tooltip>
                                <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[10px] border-warmz-glass-border/50 cursor-help">🖼️ {p.prob_image}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Imagem — fotos e imagens</TooltipContent></Tooltip>
                                <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[10px] border-warmz-glass-border/50 cursor-help">😄 {p.prob_sticker}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Figurinha — stickers do WhatsApp</TooltipContent></Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="outline" className={`text-[10px] cursor-help ${(p as any).status_enabled ? "border-primary/40 text-primary" : "border-warmz-glass-border/50 text-muted-foreground"}`}>
                                      📢 {(p as any).status_enabled ? `${(p as any).status_per_day}/dia` : "Off"}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom" className="text-xs">Status / Stories — publicações automáticas</TooltipContent>
                                </Tooltip>
                              </div>
                              {!isLast && (
                                <div className="flex items-center gap-1 text-[10px] text-primary/70 font-medium">
                                  <ChevronRight className="h-3 w-3" />
                                  <span>Próxima fase</span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Custom Profiles */}
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Perfis Customizados</p>
            {customProfiles.length === 0 ? (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                <Card className="glass-card border-warmz-glass-border/50">
                  <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-secondary/60 flex items-center justify-center mb-4">
                      <Settings2 className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">Nenhum perfil customizado</h3>
                    <p className="text-sm text-muted-foreground max-w-sm">
                      Crie seu primeiro perfil de aquecimento personalizado. Ele aparecerá como opção ao conectar ou editar números.
                    </p>
                    <Button className="mt-6 gap-2 glow-orange-sm" onClick={openCreate}>
                      <Plus className="h-4 w-4" />
                      Criar Primeiro Perfil
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {customProfiles.map((p, i) => (
                  <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                    <Card className="glass-card border-warmz-glass-border/50 hover:border-primary/30 transition-colors">
                      <CardContent className="p-5 space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{p.ui_icon || "⚙️"}</span>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-foreground">{p.name}</h3>
                                {p.chain_order != null && p.chain_order >= 0 && (
                                  <Badge variant="secondary" className="text-[10px]">Fase {(p.chain_order || 0) + 1}</Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {p.description || "Sem descrição"}
                                {" · "}
                                <span className="font-medium">{p.duration_days ? `${p.duration_days} dias` : "Tempo indeterminado"}</span>
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hover:text-destructive hover:bg-destructive/10"
                              onClick={() => deleteMutation.mutate(p.id)}
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { label: "Volume", value: `${p.min_daily_msgs}–${p.max_daily_msgs}/dia` },
                            { label: "Horário", value: `${(p.period_start || "08:00").slice(0, 5)}–${(p.period_end || "20:00").slice(0, 5)}` },
                            { label: "Delays", value: `${p.interval_min_sec}s–${p.interval_max_sec}s` },
                          ].map((d) => (
                            <div key={d.label} className="text-center p-2 rounded-lg bg-secondary/30">
                              <p className="text-[10px] text-muted-foreground uppercase">{d.label}</p>
                              <p className="text-xs font-medium text-foreground">{d.value}</p>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[10px] border-warmz-glass-border/50 cursor-help">📝 {p.prob_text}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Texto — mensagens de texto simples</TooltipContent></Tooltip>
                          <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[10px] border-warmz-glass-border/50 cursor-help">🎙️ {p.prob_audio}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Áudio — notas de voz</TooltipContent></Tooltip>
                          <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[10px] border-warmz-glass-border/50 cursor-help">🖼️ {p.prob_image}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Imagem — fotos e imagens</TooltipContent></Tooltip>
                          <Tooltip><TooltipTrigger asChild><Badge variant="outline" className="text-[10px] border-warmz-glass-border/50 cursor-help">😄 {p.prob_sticker}%</Badge></TooltipTrigger><TooltipContent side="bottom" className="text-xs">Figurinha — stickers do WhatsApp</TooltipContent></Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className={`text-[10px] cursor-help ${(p as any).status_enabled ? "border-primary/40 text-primary" : "border-warmz-glass-border/50 text-muted-foreground"}`}>
                                📢 {(p as any).status_enabled ? `${(p as any).status_per_day}/dia` : "Off"}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="text-xs">Status / Stories — publicações automáticas</TooltipContent>
                          </Tooltip>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
